import React, { useContext, useEffect } from 'react';

import {
  CarrierQuoteStateEnum,
  CustomerQuoteStateEnum,
  DistributionTypeEnum,
  EntityTypeEnum,
  ExpeditionStatusEnum,
  MakeOfferInDTO,
  QuoteDetailOutDTO,
} from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { DemandDetailView } from '../demand-detail/types';
import {
  apiPragueFromDayjsUtc,
  apiUtcFromDayjs,
  dayjsFromApiUtcInTz,
  formatDateFromDayjsWithLocale,
  formatDateTimeFromDayjsInTzWithLocale,
  formatTimeIntervalFromDayjsIntervalWithLocale,
} from '@utils/dateWithTz';
import { formatTons, formatVolume, formatWeight, isNullOrUndefined, translateQuantityCode, weightAndDimensions } from '@utils/utils';
import MyQuoteSummaryComponent from '../demand-detail/MyQuoteSummary/MyQuoteSummaryComponent';
import { useImmer } from 'use-immer';
import ReconfirmQuote from '../common/ReconfirmQuote';
import { ApiContext } from '@api/api';
import styled from 'styled-components';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';
import { useTranslation } from 'react-i18next';
import DemandStateSpan from '@components/obsolete/StateSpan/DemandStateSpan.tsx';
import AlertInfo from '@components/obsolete/Alert/AlertInfo.tsx';
import NavTitle from '@components/obsolete/NavTitle/NavTitle.tsx';
import ListInfo from '@components/obsolete/ListInfo/ListInfo.tsx';
import ListInfoItem from '@components/obsolete/ListInfo/ListInfoItem.tsx';
import HighlightedListInfoItem from '@components/obsolete/ListInfo/HighlightedListInfoItem.tsx';
import PanelWithIcon from '@components/obsolete/PanelWithIcon/PanelWithIcon.tsx';
import DisplayLocation from '@components/obsolete/DisplayLocation/DisplayLocation.tsx';
import TimeList from '@components/obsolete/TimeList/TimeList.tsx';
import TimeListItem from '@components/obsolete/TimeList/TimeListItem.tsx';
import Label from '@components/obsolete/Form/Label/Label.tsx';
import LoadInfoList from '@components/obsolete/LoadDetails/LoadInfoList/LoadInfoList.tsx';
import TimeslotsProgressTitle from '@components/obsolete/TimeslotsProgress/TimeslotsProgressTitle.tsx';
import useNavigation from '@hooks/useNavigation.tsx';
import MultipleValues from '@components/ringil3/HelperComponents/MultipleValues.tsx';
import { CreateSpotState } from '@app/pages/carrier/demand-detail/DemandDetailComponent.tsx';
import { OfferTypeEnum } from '@app/pages/carrier/demand-detail/carrierQuoteUtils.tsx';
import CarrierQuote from '@app/pages/carrier/demand-detail/CarrierQuote.tsx';
import { formatPrice } from '@utils/priceUtils.ts';
import { useCreateAttachmentComponent } from '@components/ringil3/Features/Attachments/AttachementComponent.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import dayjs from 'dayjs';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import AcceptedSummary from '@app/pages/carrier/demand-detail/AcceptedSummary.tsx';
import CollapsibleNote from '@components/RingilComponents/RichTextEditor/CollapsibleNote.tsx';
import { isCarrierQuoteBased } from '@utils/distributionType.ts';
import ContractedPriceRows from '@app/pages/carrier/demand-detail/ContractedPriceRows.tsx';

interface ComponentProps {
  action: string;
  applicationId: string;
  quoteDetail: QuoteDetailOutDTO;
  code: string;
  locationDisplayPattern: string;
}

export const QuickActionSpotDetail: React.FC<ComponentProps> = ({ action, quoteDetail, code, locationDisplayPattern }) => {
  const { t } = useTranslation('common');
  const { tr } = useTranslationLgs();
  const { caCancelDemand, caMakeQuote, caReconfirmedExpedition, listQuotationMaterialsForEntity } = useContext(ApiContext);
  const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();
  const nav = useNavigation();

  const [state, setState] = useImmer<QuoteDetailOutDTO>(quoteDetail);

  const { internals: attachmentInternals, load: loadAttachments, AttachmentComponent } = useCreateAttachmentComponent(
    {
      enableCopyDocument: false,
      enableDownloadZip: true,
      downloadZipApplicationId: quoteDetail.applicationId,
    },
    {
      entityType: EntityTypeEnum.EXPEDITION,
      entityId: quoteDetail.expeditionId,
      disabled: true,
      loadAttachmentsForEntity: listQuotationMaterialsForEntity,
      securityCode: code,
    }
  );

  useEffect(() => {
    loadAttachments(EntityTypeEnum.EXPEDITION, quoteDetail.expeditionId);
  }, []);

  const disabled = state.isExpeditionCanceled || state.isManualExpedition;

  const quoteState = isCarrierQuoteBased(state.distributionType) ? state.carrierQuoteState : state.customerQuoteState;
  const createSpot =
    isCarrierQuoteBased(state.distributionType) &&
    (quoteState === CarrierQuoteStateEnum.NEW || (quoteState === CarrierQuoteStateEnum.REJECTED_BY_CARRIER && !state.isAssignedToAnotherCarrier));


  const stateValidTill = (state: QuoteDetailOutDTO): dayjs.Dayjs => {
    return state.timezoneId != null
      ? dayjsFromApiUtcInTz(state.utcEnd, state.timezoneId)
      : dayjsFromApiUtcInTz(state.distributionValidTill, 'Europe/Prague');
  }

  const rejectSpot = (customerId: string, applicationId: string, quoteId: string) => {
    caCancelDemand(
      customerId,
      applicationId,
      quoteId,
      () => {
        window.location.reload();
      },
      code,
      { onValidationFailed: e => setBeValidationErrors(e) },
    );
  };

  const makeQuote = (customerId: string, applicationId: string, quoteId: string, data: CreateSpotState, onFinish: () => void) => {
    const body: MakeOfferInDTO = {
      price: data.price,
      currency: data.currency,
      validTill: apiPragueFromDayjsUtc(data.selectedData, 'Europe/Prague'),
      utcEnd: apiUtcFromDayjs(data.utcEnd),
      timezoneId: data.timezoneId,
      units: data.partialDeliveryUnits,
      note: data.note,
      transportLegs: data.transportLegs,
      timeslots: data.timeslots,
    };
    caMakeQuote(
      customerId,
      applicationId,
      quoteId,
      body,
      () => {
        onFinish();
        window.location.reload();
      },
      code,
      {
        onValidationFailed: v => {
          setBeValidationErrors(v);
          onFinish();
        },
      },
    );
  };

  const reconfirm = (companyId: string, applicationId: string) => {
    caReconfirmedExpedition(
      companyId,
      applicationId,
      () => {
        window.location.reload();
      },
      code,
      { onValidationFailed: e => setBeValidationErrors(e) },
    );
  };

  const resolveExpeditionStatus = (state: DemandDetailView) => {
    const validTill = state.timezoneId != null
      ? formatDateTimeFromDayjsInTzWithLocale(dayjsFromApiUtcInTz(state.utcEnd, state.timezoneId), state.userTimezoneId || state.timezoneId)
      : formatDateTimeFromDayjsInTzWithLocale(dayjsFromApiUtcInTz(state.distributionValidTill, 'Europe/Prague'), 'Europe/Prague');
    return (
      <>
        <DemandStateSpan demandState={state.customerQuoteState} isManual={state.isManualExpedition} isCanceled={quoteDetail.isExpeditionCanceled} />
        {!disabled && (
          <>
            {(state.carrierQuoteState === CarrierQuoteStateEnum.ACCEPTED_NEED_RECONFIRM || !isNullOrUndefined(state.reconfirmedAt)) &&
              !state.isAssignedToAnotherCarrier && (
                <div className="p-mb-3">
                  <ReconfirmQuote
                    onReconfirm={() => reconfirm(state.companyId, state.applicationId)}
                    reconfirmedAtUtc={state.reconfirmedAtUtc}
                    timezoneId={state.timezoneId}
                    isReconfirmedByCarrier={state.isReconfirmedByCarrier}
                    disabled={state.isExpeditionCanceled || state.isManualExpedition}
                  />
                </div>
              )}
            <div>
              {t('DemandDetailComponent.validTill', 'Platná do ')}
              <strong>{validTill}</strong>
              <COTimezone timezoneId={state.userTimezoneId || state.timezoneId} />
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <>
      <BackendValidationComponent />
      {(quoteState == CustomerQuoteStateEnum.ACCEPTED_BY_CARRIER ||
        quoteState == CarrierQuoteStateEnum.ACCEPTED_BY_CUSTOMER ||
        quoteState === CarrierQuoteStateEnum.ACCEPTED_NEED_RECONFIRM) &&
        !disabled && (
          <>
            <AlertInfo iconName="check" backgroundWrapper="cyan">
              <p>{t('DemandDetailComponent.youWon', 'Customer assigned you as a carrier to execute this shipment.')}</p>
            </AlertInfo>
            <AlertInfo backgroundWrapper="transparent">
              <div className="p-mr-3">
                <p>{t('DemandDetailComponent.findDetailInShipments', 'You can find detail in all shipments list or here')}</p>
              </div>
              {state.childExpeditions && state.childExpeditions.length > 0 ? (
                <div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {[...state.childExpeditions]
                      .filter(exp => exp.expeditionStatus !== ExpeditionStatusEnum.CANCELED)
                      .sort((a, b) => a.applicationId.localeCompare(b.applicationId))
                      .map(item => (
                        <RingilButton
                          key={`${item.customerId}-${item.applicationId}`}
                          mode="filled-rectangle"
                          label={item.applicationId}
                          onClick={() =>
                            nav.navigate(
                              nav.urlFunctions.createCarrierShipmentDetail(item.customerId, item.applicationId),
                            )
                          }
                        />
                      ))}
                  </div>
                  <div className="mt-2">
                    <AcceptedSummary childExpeditions={
                      [...state.childExpeditions]
                        .filter(exp => exp.expeditionStatus !== ExpeditionStatusEnum.CANCELED)
                    }
                    />
                  </div>
                </div>
              ) : (
                <RingilButton
                  mode="filled-rectangle"
                  label={t('DemandDetailComponent.goToShipmentDetail', 'Go to shipment details')}
                  onClick={() =>
                    nav.navigate(
                      nav.urlFunctions.createCarrierShipmentDetail(state.companyId, state.applicationId)
                    )
                  }
                />
              )}
            </AlertInfo>
          </>
        )}
      <div className="container-wrapper-lg">
        <div className="flex flex-col lg:flex-row gap-4 items-start">
          <div className="w-full lg:flex-1 lg:min-w-0">
            <NavTitle wrapper="WITHOUT-COL_PADDING-BOOTOM-4">
              <h2 className="h2-title p-m-0">
                {t('demandDetail.pageTitle', {
                  applicationId: state.applicationId,
                })}
                <span></span>
              </h2>
              <p>{resolveExpeditionStatus(state)}</p>
            </NavTitle>

            <ListInfo>
              <ListInfoItem title={t(`DemandDetailComponent.distance`, `Distance`)}>
                <p>
                  <small>{state.distance ? state.distance : '-'} km</small>
                </p>
              </ListInfoItem>
              <HighlightedListInfoItem title={t(`DemandDetailComponent.noteToCarriers`, `Note to carriers`)} highlighted={!isNullOrUndefined(state.carrierNote)}>
                {!isNullOrUndefined(state.carrierNote) ? (
                  <CollapsibleNote html={state.carrierNote} />
                ) : (
                  <p>
                    <small>-</small>
                  </p>
                )}
              </HighlightedListInfoItem>
              <HighlightedListInfoItem title={t('RouteDescriptionComponent.requirements', 'Požadavky')} highlighted={state.requirements?.length > 0}>
                <p>
                  <small>{state.requirements?.length > 0 ? state.requirements.join(', ') : '-'}</small>
                </p>
              </HighlightedListInfoItem>
              <ListInfoItem title={t(`DemandDetailComponent.expeditionType`, `Expedition type`)}>
                <p>
                  <small>{state.expeditionType ? t(`global.expeditionType${state.expeditionType}`) : t('global.expeditionTypeNotSpecified', 'Neuvedeno')}</small>
                </p>
              </ListInfoItem>
              {state.truckType && (
                <ListInfoItem title={t(`DemandDetailComponent.truckType`, `Truck type`)}>
                  <p>
                    <small>{state.truckType}</small>
                  </p>
                </ListInfoItem>
              )}
              <ListInfoItem title={t(`DemandDetailComponent.maximumWeight`, `Maximum weight`)}>
                <p>
                  <small>{state.maxWeight.toLocaleString()} kg</small>
                </p>
              </ListInfoItem>
              <ListInfoItem title={t(`DemandDetailComponent.cargoType`, `Cargo type`)}>
                <p>
                  <small>{state.cargoType}</small>
                  {state.adrWeight && (
                    <small> - {formatWeight(state.adrWeight)}</small>
                  )}
                </p>
              </ListInfoItem>
              {state.distributionType === DistributionTypeEnum.SPOT && (
                <ListInfoItem title={t(`DemandDetailComponent.expectedPrice`, `Expected price`)}>
                  <p>
                    <small>{formatPrice(state.price, state.currency)}</small>
                  </p>
                </ListInfoItem>
              )}
              {state.distributionType === DistributionTypeEnum.CONTRACTED && (
                <>
                  <ListInfoItem title={tr(`carrier.demand.quote.contracted.distributionType.title`, `Typ poptávky`)}>
                    <p>
                      <small>{tr(`carrier.demand.quote.contracted.distributionType.value`, `Smluvní dopravce`)}</small>
                    </p>
                  </ListInfoItem>
                  <ListInfoItem title={tr(`carrier.demand.quote.contracted.price.title`, `Smluvní cena`)}>
                    <p>
                      <small>
                        <ContractedPriceRows contracted={state.contracted} />
                      </small>
                    </p>
                  </ListInfoItem>
                </>
              )}
              {state.incoterms && (
                <ListInfoItem title={t(`DemandDetailComponent.incoterms`, `Incoterms`)}>
                  <p>
                    <small>
                      <MultipleValues
                        values={[state.incoterms.incotermsValue, state.incoterms.incotermsDescription]}></MultipleValues>
                    </small>
                  </p>
                </ListInfoItem>
              )}
            </ListInfo>
            {state.timeslots.map((location, locationIndex) => {
              return (
                <div className="timeslots-wrapper" key={locationIndex}>
                  <PanelWithIcon numberIcon={`${locationIndex + 1}`} wrapper="SM">
                    <p>
                      <DisplayLocation location={location} locationDisplayPattern={locationDisplayPattern} />
                    </p>
                  </PanelWithIcon>
                  <div className="time-shell">
                    <TimeList>
                      {location.timeslots.map((timeslot, timeslotIndex) => {
                        const slotSince = timeslot.timezoneId != null ? timeslot.utcStart : timeslot.sinceInUTC;
                        const slotTill = timeslot.timezoneId != null ? timeslot.utcEnd : timeslot.tillInUTC;
                        const timezoneId =  timeslot.timezoneId ?? 'Europe/Prague';
                        return (
                          <TimeListItem
                            key={timeslotIndex}
                            date={formatDateFromDayjsWithLocale(dayjsFromApiUtcInTz(slotSince, timezoneId))}
                            isWholeDay={timeslot.isNotSpecifiedTime}
                            time={`${formatTimeIntervalFromDayjsIntervalWithLocale(dayjsFromApiUtcInTz(slotSince, timezoneId), dayjsFromApiUtcInTz(slotTill, timezoneId), timezoneId)}`}
                            selected={timeslot.isSelected}
                            timezoneId={timeslot.timezoneId}
                          />
                        );
                      })}
                    </TimeList>
                  </div>
                </div>
              );
            })}
            <div className="shipment-requirements-wrapper">
              <Label title={t('RouteDescriptionComponent.manipulationType', 'Manipulace')} />
              <p>
                <i className="p-mr-2 pi pi-check"></i>
                <span>{state.loadingTypes && state.loadingTypes.map(type => t(`RouteDescriptionComponent.manipulatioType.${type}`)).join(', ')}</span>
              </p>
            </div>
            {state.items && state.items.length > 0 && (
              <div className="load-info-wrapper load-info-wrapper--sm  p-mt-4 p-mb-4 p-d-flex p-flex-wrap">
                {state.items.map((cargo, cargoIndex) => {
                  return (
                    <LoadInfoList
                      key={cargoIndex}
                      title={`${cargo.name} (${cargo.amount} ${translateQuantityCode(cargo.quantityUnit, tr)})`}
                      description={formatTons(cargo.weight)}
                      stackable={cargo.stackable}
                      itemNote={cargo.itemNote}
                    />
                  );
                })}
              </div>
            )}
            <div className="p-mt-5 list-routes">
              {state.locations.map((location, locationIndex) => {
                return (
                  <React.Fragment key={locationIndex}>
                    <TimeslotsProgressTitle
                      number={`${locationIndex + 1}`}
                      address={<DisplayLocation location={location} locationDisplayPattern={locationDisplayPattern} />}
                      timezoneId={location.timezoneId}
                    />

                    {location.requirementCodes && location.requirementCodes.length > 0 && (
                      <div className="pb-2 gap-1">
                        <strong>{t('RouteDescriptionComponent.requirements', 'Požadavky')}:</strong>
                        {location.requirementCodes.map(req => tr('specialRequirement.' + req, req)).join(', ')}
                      </div>
                    )}

                    {location.loadedOrders && location.loadedOrders.length > 0 && (
                      <div className="p-d-flex">
                        <PanelWithIcon srcIcon="/images/icon_pickup.png">
                          <Label title={t('RouteDescriptionComponent.pickUp', 'Nakládka')} />

                          {location.loadedOrders && location.loadedOrders.length > 0 && (
                            <>
                              {location.loadedOrders.map((loadedOrder, loadedOrderKey) => {
                                return (
                                  <Col>
                                    {loadedOrder.orderName && (
                                      <Row>
                                        <ListInfoItem title={t(`RouteDescriptionComponent.order`, `Objednavka`)}>
                                          <p>
                                            <small>{loadedOrder.orderName}</small>
                                          </p>
                                        </ListInfoItem>
                                        <ListInfoItem title={t(`RouteDescriptionComponent.weight`, `Hmotnost`)}>
                                          <p>
                                            <small>{formatWeight(loadedOrder.totalWeight)}</small>
                                          </p>
                                        </ListInfoItem>
                                        {state.carrierQuoteSettings?.volumeOfOrderEnabled && (
                                          <ListInfoItem title={t(`RouteDescriptionComponent.volume`, `Objem`)}>
                                            <p>
                                              <small>{formatVolume(loadedOrder.totalVolume)}</small>
                                            </p>
                                          </ListInfoItem>
                                        )}
                                      </Row>
                                    )}
                                    {loadedOrder.items.map((cargo, cargoIndex) => {
                                      return (
                                        <LoadInfoList
                                          key={cargoIndex}
                                          title={`${cargo.name} (${cargo.amount} ${translateQuantityCode(cargo.quantityUnit, tr)})`}
                                          description={weightAndDimensions(cargo.height, cargo.length, cargo.width, cargo.weight, t)}
                                          stackable={cargo.stackable}
                                          itemNote={cargo.itemNote}
                                        />
                                      );
                                    })}
                                  </Col>
                                );
                              })}
                            </>
                          )}
                        </PanelWithIcon>
                      </div>
                    )}
                    {location.unloadedOrders && location.unloadedOrders.length > 0 && (
                      <div className="p-d-flex">
                        <PanelWithIcon srcIcon="/images/icon_dropoff.png">
                          <Label title={t('RouteDescriptionComponent.dropoff', 'Vykládka')} />

                          {location.unloadedOrders && location.unloadedOrders.length > 0 && (
                            <>
                              {location.unloadedOrders.map((loadedOrder, loadedOrderKey) => {
                                return (
                                  <Col>
                                    {loadedOrder.orderName && (
                                      <Row>
                                        <ListInfoItem title={t(`RouteDescriptionComponent.order`, `Objednavka`)}>
                                          <p>
                                            <small>{loadedOrder.orderName}</small>
                                          </p>
                                        </ListInfoItem>
                                        <ListInfoItem title={t(`RouteDescriptionComponent.weight`, `Hmotnost`)}>
                                          <p>
                                            <small>{formatWeight(loadedOrder.totalWeight)}</small>
                                          </p>
                                        </ListInfoItem>
                                        {state.carrierQuoteSettings?.volumeOfOrderEnabled && (
                                          <ListInfoItem title={t(`RouteDescriptionComponent.volume`, `Objem`)}>
                                            <p>
                                              <small>{formatVolume(loadedOrder.totalVolume)}</small>
                                            </p>
                                          </ListInfoItem>
                                        )}
                                      </Row>
                                    )}
                                    {loadedOrder.items.map((cargo, cargoIndex) => {
                                      return (
                                        <LoadInfoList
                                          key={cargoIndex}
                                          title={`${cargo.name} (${cargo.amount} ${translateQuantityCode(cargo.quantityUnit, tr)})`}
                                          description={weightAndDimensions(cargo.height, cargo.length, cargo.width, cargo.weight, t)}
                                          stackable={cargo.stackable}
                                          itemNote={cargo.itemNote}
                                        />
                                      );
                                    })}
                                  </Col>
                                );
                              })}
                            </>
                          )}
                        </PanelWithIcon>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            <div className="p-mt-5">
              <h3 className="p-mt-0" style={{ fontWeight: 'normal' }}>
                {t('RouteDescriptionComponent.customer', 'Poptávající')}
              </h3>
              <PanelWithIcon srcIcon="/images/contacts.svg">
                <Label title={state.customer.name} />
                <p>
                  {state.customer.streetNr}, {state.customer.city}, {state.customer.country}
                </p>
                <p>
                  {t('RouteDescriptionComponent.CrnAndTin', 'IČ: 28441345 | DIČ: CZ28441345', {
                    crn: state.customer.crn || '-',
                    tin: state.customer.tin || '-',
                  })}
                </p>
              </PanelWithIcon>
              <PanelWithIcon srcIcon="/images/user.svg">
                <p>{state.responsiblePerson.fullName}</p>
                <p className="text-gray5">
                  <small>{t('RouteDescriptionComponent.responsiblePerson', 'odpovědná osoba')}</small>
                </p>
              </PanelWithIcon>
            </div>
          </div>
          <div className="w-full min-w-[450px] lg:flex-1 lg:w-auto">
            {state.distributionType === DistributionTypeEnum.FIXED_PRICE && state.customerQuoteState !== CustomerQuoteStateEnum.NEW && (
              <MyQuoteSummaryComponent
                distributionType={DistributionTypeEnum[state.distributionType]}
                enableReject={false}
                onReject={() => {}}
                enableChangePrice={false}
                enableChangeTimeslots={false}
                price={state.price}
                currency={state.currency}
                quoteValidTill={stateValidTill(state)}
                timeslots={state.timeslots}
                code={code}
                timezoneId={state.timezoneId}
              />
            )}
            <CarrierQuote
              quote={state}
              offerType={OfferTypeEnum.SPOT}
              onReject={() => rejectSpot(state.companyId, state.applicationId, state.quoteId)}
              onAccept={(data, onFinish) => makeQuote(state.companyId, state.applicationId, state.quoteId, data, onFinish)}
              code={code}
              initialAction={action}
            />
          </div>
        </div>
        <AttachmentComponent
          internals={attachmentInternals}
          entityId={quoteDetail.expeditionId}
          entityType={EntityTypeEnum.EXPEDITION}
          disabledMassAction
          disabledDelete
          allowAddNewAttachment={false}
          allowEditAttachment={false}
        />
      </div>
    </>
  );
};

const Col = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
`;
